import React from "react";
import { Link } from "react-router-dom";

export default function PrivacyPolicy() {
  return (
    <>
      <div className="relative">
        <div
          className="bg-cover bg-center text-center text-white p-8"
          style={{
            backgroundImage: `url("./images/innerb.jpg")`,
          }}
        >
          <div className="text-center pt-40 pb-28">
            <h1 className="text-2xl md:text-4xl font-bold">
              Politique de Confidentialité & Cookies
            </h1>
            <p className="text-white/80 mt-2 text-sm md:text-base">
              Dernière mise à jour : 21 juillet 2024
            </p>
          </div>
        </div>
      </div>

      <div className="container mx-auto my-12 px-4 max-w-5xl">
        <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-6 md:p-12 text-[#546E7A] leading-relaxed space-y-8">
          {/* Header Highlight Banner */}
          <div className="bg-[#EDF2F6] p-5 md:p-6 rounded-xl border border-gray-200">
            <h2 className="text-lg md:text-xl font-bold text-[#020220] mb-2">
              Protection des Données chez SEVA
            </h2>
            <p className="text-sm md:text-base">
              Le 1er septembre 2023, la nouvelle loi suisse sur la protection des données (<strong>nLPD</strong>) est entrée en vigueur pour renforcer la protection des personnes privées et s'adapter au règlement général sur la protection des données de l'UE (<strong>RGPD</strong>). Chez SEVA, la protection de vos données personnelles et médicales est une priorité absolue.
            </p>
          </div>

          {/* Section 1: Principes */}
          <section className="space-y-4">
            <h2 className="text-xl md:text-2xl font-bold text-[#020220] flex items-center gap-3">
              <span className="w-8 h-8 rounded-full bg-[#037897]/10 text-[#037897] flex items-center justify-center text-sm font-bold flex-shrink-0">1</span>
              Les Principes Fondamentaux
            </h2>
            <div className="pl-11 space-y-3 text-sm md:text-base">
              <p>
                <strong>Obligations d'information :</strong> Nous élaborons une déclaration transparente et accessible comprenant l'identité du responsable, les coordonnées de contact, les finalités du traitement et toute transmission éventuelle de données à des tiers autorisés.
              </p>
              <p>
                <strong>Données sensibles & "Privacy by Design" :</strong> Les données relatives à la santé font l'objet du niveau de protection le plus strict. Nous appliquons les principes de <em>Privacy by Design</em> (protection intégrée dès la conception) et <em>Privacy by Default</em> (paramètres par défaut les plus favorables à la confidentialité).
              </p>
              <p>
                <strong>Registre et sous-traitance :</strong> Nous tenons un registre exhaustif des traitements de données et garantissons contractuellement que tous nos prestataires et sous-traitants respectent les mêmes normes de sécurité strictes.
              </p>
            </div>
          </section>

          {/* Section 2: Déclaration */}
          <section className="space-y-4">
            <h2 className="text-xl md:text-2xl font-bold text-[#020220] flex items-center gap-3">
              <span className="w-8 h-8 rounded-full bg-[#037897]/10 text-[#037897] flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
              Déclaration à l'Attention du Public
            </h2>
            <div className="pl-11 space-y-3 text-sm md:text-base">
              <p>
                Chez SEVA, nous nous engageons à protéger vos données personnelles avec le niveau de sécurité le plus élevé au sein de notre organisation et conformément aux lois en vigueur.
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Pour les résidents suisses : respect de la <strong>nouvelle loi fédérale suisse sur la protection des données (nLPD)</strong> du 25 septembre 2020.</li>
                <li>Pour les résidents de l'Union Européenne : respect complémentaire du <strong>Règlement Général sur la Protection des Données (RGPD)</strong>.</li>
              </ul>
            </div>
          </section>

          {/* Section 3: Où en est SEVA */}
          <section className="space-y-4">
            <h2 className="text-xl md:text-2xl font-bold text-[#020220] flex items-center gap-3">
              <span className="w-8 h-8 rounded-full bg-[#037897]/10 text-[#037897] flex items-center justify-center text-sm font-bold flex-shrink-0">3</span>
              Traitement & Sécurité des Données
            </h2>
            <div className="pl-11 space-y-3 text-sm md:text-base">
              <p>
                <strong>Traitement des données :</strong> Les données personnelles ne sont collectées et traitées qu'à des fins déterminées, explicites et légitimes (gestion des programmes, suivi thérapeutique, consultations 1:1, facturation et sécurité). Les données sont cartographiées pour permettre leur mise à jour, rectification ou suppression sur simple demande.
              </p>
              <p>
                <strong>Sécurité des données :</strong> Nous mettons en œuvre toutes les mesures techniques et organisationnelles appropriées (chiffrement, contrôles d'accès stricts, hébergement sécurisé) pour garantir la confidentialité et l'intégrité des informations.
              </p>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mt-2 space-y-2 text-xs md:text-sm">
                <p>
                  Pour en savoir plus sur la législation suisse relative à la protection des données :{" "}
                  <a
                    href="https://www.fedlex.admin.ch/eli/cc/2022/491/fr"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#037897] font-semibold hover:underline"
                  >
                    Site de la Confédération suisse (Fedlex) &rarr;
                  </a>
                </p>
                <p>
                  Pour en savoir plus sur le règlement européen (RGPD) :{" "}
                  <a
                    href="https://ec.europa.eu/competition/general/data_protection_en.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#037897] font-semibold hover:underline"
                  >
                    Commission européenne (RGPD) &rarr;
                  </a>
                </p>
              </div>
            </div>
          </section>

          {/* Section 4: Vos Droits */}
          <section className="space-y-4">
            <h2 className="text-xl md:text-2xl font-bold text-[#020220] flex items-center gap-3">
              <span className="w-8 h-8 rounded-full bg-[#037897]/10 text-[#037897] flex items-center justify-center text-sm font-bold flex-shrink-0">4</span>
              Information sur Vos Droits
            </h2>
            <div className="pl-11 space-y-3 text-sm md:text-base">
              <p>
                Conformément à la législation suisse et européenne, vous disposez des droits suivants :
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                <div className="p-4 rounded-lg border border-gray-200 bg-white shadow-sm">
                  <h3 className="font-bold text-[#020220] mb-1">Droit d'accès</h3>
                  <p className="text-xs text-[#546E7A]">Savoir si nous traitons vos données, quelles catégories sont traitées et demander l'accès à ces informations.</p>
                </div>
                <div className="p-4 rounded-lg border border-gray-200 bg-white shadow-sm">
                  <h3 className="font-bold text-[#020220] mb-1">Droit de rectification</h3>
                  <p className="text-xs text-[#546E7A]">Demander la correction immédiate de toute donnée inexacte ou incomplète.</p>
                </div>
                <div className="p-4 rounded-lg border border-gray-200 bg-white shadow-sm">
                  <h3 className="font-bold text-[#020220] mb-1">Droit à l'effacement</h3>
                  <p className="text-xs text-[#546E7A]">Demander la suppression de vos données personnelles lorsqu'elles ne sont plus nécessaires.</p>
                </div>
                <div className="p-4 rounded-lg border border-gray-200 bg-white shadow-sm">
                  <h3 className="font-bold text-[#020220] mb-1">Opposition au traitement</h3>
                  <p className="text-xs text-[#546E7A]">Vous opposer au traitement de vos données ou retirer votre consentement à des fins de marketing à tout moment.</p>
                </div>
                <div className="p-4 rounded-lg border border-gray-200 bg-white shadow-sm md:col-span-2">
                  <h3 className="font-bold text-[#020220] mb-1">Limitation du traitement</h3>
                  <p className="text-xs text-[#546E7A]">Demander la restriction temporaire du traitement de vos données personnelles sous certaines conditions légales.</p>
                </div>
              </div>
              <div className="pt-2 text-xs text-gray-500">
                Références officielles :{" "}
                <a
                  href="https://www.edoeb.admin.ch/edoeb/fr/home/datenschutz/grundlagen/ndsg.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#037897] hover:underline"
                >
                  PFPDT Suisse (EDOEB)
                </a>{" "}
                &bull;{" "}
                <a
                  href="https://commission.europa.eu/law/law-topic/data-protection/data-protection-eu_fr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#037897] hover:underline"
                >
                  Protection des données UE
                </a>
              </div>
            </div>
          </section>

          {/* Section 5: Cookies et Contact */}
          <section className="space-y-4">
            <h2 className="text-xl md:text-2xl font-bold text-[#020220] flex items-center gap-3">
              <span className="w-8 h-8 rounded-full bg-[#037897]/10 text-[#037897] flex items-center justify-center text-sm font-bold flex-shrink-0">5</span>
              Cookies & Questions
            </h2>
            <div className="pl-11 space-y-3 text-sm md:text-base">
              <p>
                Notre site utilise des cookies essentiels au fonctionnement de la plateforme (session, sécurité, authentification) ainsi que des cookies de performance analytique anonymisés pour améliorer votre expérience utilisateur.
              </p>
              <div className="pt-4 border-t border-gray-100 flex flex-wrap items-center justify-between gap-4">
                <p className="text-xs text-gray-500">
                  Pour exercer vos droits ou pour toute question : <a href="mailto:info@centre-seva.com" className="text-[#037897] font-semibold hover:underline">info@centre-seva.com</a>
                </p>
                <div className="flex gap-4">
                  <Link to="/terms" className="text-xs font-semibold text-[#037897] hover:underline uppercase tracking-wider">
                    Conditions Générales &rarr;
                  </Link>
                  <Link to="/contact" className="text-xs font-semibold text-[#037897] hover:underline uppercase tracking-wider">
                    Contact &rarr;
                  </Link>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}
